import React, { useState, useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import { Search, History, TrendingUp, Calculator, BookOpen, Star, Building2, BarChart2, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackMetaEvent } from '../services/metaConversions';

const POPULAR_TICKERS = [
  { ticker: 'PETR4', name: 'Petrobras PN' },
  { ticker: 'VALE3', name: 'Vale ON' },
  { ticker: 'ITUB4', name: 'Itaú Unibanco PN' },
  { ticker: 'BBDC4', name: 'Bradesco PN' },
  { ticker: 'ABEV3', name: 'Ambev ON' },
  { ticker: 'WEGE3', name: 'WEG ON' },
  { ticker: 'BBAS3', name: 'Banco do Brasil ON' },
  { ticker: 'AAPL', name: 'Apple Inc.' },
  { ticker: 'MSFT', name: 'Microsoft Corp.' },
  { ticker: 'NVDA', name: 'NVIDIA Corp.' },
  { ticker: 'GOOGL', name: 'Alphabet Inc.' }
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tickers] = useState<{ ticker: string; name: string }[]>(POPULAR_TICKERS);
  const [allTickers, setAllTickers] = useState<{ ticker: string; name: string, isBr: boolean }[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    
    const handleCustomEvent = () => setOpen(true);
    
    document.addEventListener('keydown', down);
    document.addEventListener('open-command-palette', handleCustomEvent);
    
    return () => {
      document.removeEventListener('keydown', down);
      document.removeEventListener('open-command-palette', handleCustomEvent);
    };
  }, []);

  // Lazy load dictionary when palette is opened
  useEffect(() => {
    if (open && !allTickers) {
      import('../data/tickers_dictionary.json').then((mod) => {
         const dict = mod.default || mod;
         const br = (dict.br || []).map((t: any) => ({ ticker: t.ticker, name: t.name, isBr: true }));
         const us = (dict.us || []).map((t: any) => ({ ticker: t.ticker, name: t.name, isBr: false }));
         setAllTickers([...br, ...us]);
      }).catch(err => console.error("Failed to load dictionary", err));
    }
  }, [open, allTickers]);

  const handleSelect = (route: string) => {
    setOpen(false);
    setSearch('');
    trackMetaEvent('Search', { search_string: route });
    navigate(route);
  };

  if (!open) return null;

  // Optimized local inductive search with scoring
  const filteredTickers = useMemo(() => {
    if (!search) return tickers.slice(0, 5);
    const searchLower = search.toLowerCase();
    const sourceList = allTickers || tickers;
    
    const results = [];
    for (let i = 0; i < sourceList.length; i++) {
       const t = sourceList[i];
       const tickerLower = (t.ticker || '').toLowerCase();
       const nameLower = (t.name || '').toLowerCase();
       
       if (tickerLower.includes(searchLower) || nameLower.includes(searchLower)) {
           let score = 0;
           // Exact match
           if (tickerLower === searchLower) score = 100;
           // Prefix matches
           else if (tickerLower.startsWith(searchLower)) score = 50;
           else if (nameLower.startsWith(searchLower)) score = 30;
           // Substring matches
           else score = 10;
           
           // Boost BR stocks
           if ((t as any).isBr) score += 5; 
           
           results.push({ ...t, score });
       }
    }
    
    return results.sort((a, b) => b.score - a.score).slice(0, 8);
  }, [search, allTickers, tickers]);

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] sm:pt-[20vh] px-4 backdrop-blur-sm bg-slate-900/40" onClick={() => setOpen(false)}>
      <div 
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <Command label="Command Menu" className="w-full" shouldFilter={false}>
          <div className="flex items-center px-4 border-b border-slate-100 dark:border-slate-800">
            <Search className="w-5 h-5 text-slate-400 mr-2" />
            <Command.Input 
              value={search}
              onValueChange={setSearch}
              autoFocus
              placeholder="Digite um ticker (ex: PETR4) ou pesquise ferramentas..." 
              className="w-full py-4 bg-transparent border-none outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400 text-base"
            />
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-2 scroll-smooth">
            <Command.Empty className="py-6 text-center text-sm text-slate-500">
              Nenhum resultado encontrado.
            </Command.Empty>

            {filteredTickers.length > 0 && (
              <Command.Group heading="Ações" className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {filteredTickers.map(t => (
                  <Command.Item 
                    key={t.ticker}
                    onSelect={() => handleSelect(`/acao/${t.ticker}`)}
                    className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 aria-selected:bg-slate-100 dark:aria-selected:bg-slate-800 text-slate-700 dark:text-slate-200"
                  >
                    <Building2 className="w-4 h-4 mr-3 text-slate-400" />
                    <div>
                      <span className="font-bold">{t.ticker}</span>
                      <span className="ml-2 text-slate-500 truncate text-xs">{t.name}</span>
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {!search && (
              <>
                <Command.Group heading="Análise e Ferramentas" className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <Command.Item onSelect={() => handleSelect('/compare')} className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <BarChart2 className="w-4 h-4 mr-3 text-blue-500" /> Comparar Ações
                  </Command.Item>
                  <Command.Item onSelect={() => handleSelect('/rankings')} className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <TrendingUp className="w-4 h-4 mr-3 text-emerald-500" /> Rankings Fundamentalistas
                  </Command.Item>
                  <Command.Item onSelect={() => handleSelect('/calculator')} className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <Calculator className="w-4 h-4 mr-3 text-amber-500" /> Calculadora de Valuation Simples
                  </Command.Item>
                  <Command.Item onSelect={() => handleSelect('/portfolio')} className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <Briefcase className="w-4 h-4 mr-3 text-purple-500" /> Simulador de Carteira
                  </Command.Item>
                </Command.Group>
                
                <Command.Group heading="Educação" className="px-2 py-1.5 mt-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <Command.Item onSelect={() => handleSelect('/educacao/o-que-e-valuation')} className="flex items-center px-3 py-2 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
                    <BookOpen className="w-4 h-4 mr-3 text-slate-400" /> O que é Valuation?
                  </Command.Item>
                  <Command.Item onSelect={() => handleSelect('/educacao/fluxo-de-caixa-descontado')} className="flex items-center px-3 py-2 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
                    <BookOpen className="w-4 h-4 mr-3 text-slate-400" /> Fluxo de Caixa Descontado (DCF)
                  </Command.Item>
                </Command.Group>
              </>
            )}
            
            {search && filteredTickers.length === 0 && (
              <Command.Group heading="Sugestões" className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                 <Command.Item onSelect={() => handleSelect('/rankings')} className="flex items-center px-3 py-3 min-h-[44px] mt-1 text-sm rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                    <TrendingUp className="w-4 h-4 mr-3 text-emerald-500" /> Ver Rankings
                  </Command.Item>
              </Command.Group>
            )}

          </Command.List>
          
          <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-400">Use <kbd className="font-sans px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500">↑</kbd> <kbd className="font-sans px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500">↓</kbd> para navegar</span>
            <span className="text-xs text-slate-400"><kbd className="font-sans px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500">Enter</kbd> para selecionar</span>
          </div>
        </Command>
      </div>
    </div>
  );
};
